/* Collection-only Buy More, Save More builder. */
var bastionCollectionDiscountCodeKey = (code) => String(code || '').trim().toLowerCase();

var bastionCollectionTierCodes = (offer) => new Set([
  offer?.dataset?.tier1DiscountCode,
  offer?.dataset?.tier2DiscountCode,
  offer?.dataset?.tier3DiscountCode,
].map(bastionCollectionDiscountCodeKey).filter(Boolean));

var bastionApplyCollectionTierDiscount = async (offer, discountCode, sections = [], currentCart = null) => {
  if (typeof window.bastionReconcileVolumeDiscount === 'function') {
    window.bastionResumeAutomaticVolumeDiscount?.();
    return window.bastionReconcileVolumeDiscount(currentCart, sections);
  }

  const code = String(discountCode || '').trim();
  if (!code) return { cart: currentCart, error: '' };

  const root = window.Shopify?.routes?.root || '/';
  const cart = currentCart || await fetch(`${root}cart.js`).then((response) => response.json());
  const configuredTierCodes = bastionCollectionTierCodes(offer);
  const activeCodes = Array.isArray(cart?.discount_codes)
    ? cart.discount_codes.filter((discount) => discount.applicable).map((discount) => discount.code)
    : [];
  const nextCodes = activeCodes.filter((activeCode) => !configuredTierCodes.has(
    bastionCollectionDiscountCodeKey(activeCode)
  ));

  if (!nextCodes.some((activeCode) => (
    bastionCollectionDiscountCodeKey(activeCode) === bastionCollectionDiscountCodeKey(code)
  ))) {
    nextCodes.push(code);
  }

  const response = await fetch(`${root}cart/update.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({
      discount: nextCodes.join(','),
      sections: sections.join(','),
    }),
  });
  const responseJson = await response.json();

  if (!response.ok) {
    return {
      cart,
      error: responseJson.description || responseJson.message || `The discount code “${code}” could not be applied.`,
    };
  }

  if (Array.isArray(responseJson.discount_codes)) {
    const selectedDiscount = responseJson.discount_codes.find(
      (discount) => bastionCollectionDiscountCodeKey(discount.code) === bastionCollectionDiscountCodeKey(code)
    );

    if (!selectedDiscount?.applicable) {
      return {
        cart: responseJson,
        error: `The offer was added, but “${code}” could not be applied. Review the discount requirements in your cart.`,
      };
    }
  }

  return { cart: responseJson, error: '' };
};

if (!customElements.get('bastion-collection-volume-offer')) {
  customElements.define(
    'bastion-collection-volume-offer',
    class extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        if (this.cardCopyFitFrame) cancelAnimationFrame(this.cardCopyFitFrame);
        this.cardCopyFitTimers?.forEach((timer) => clearTimeout(timer));
        this.abortController = new AbortController();
        this.dialog = this.querySelector('[data-offer-dialog]');
        this.cardCopy = this.querySelector('.bastion-collection-offer__card-copy');
        this.cardHeading = this.querySelector('.bastion-collection-offer__card-title');
        this.cardText = this.querySelector('.bastion-collection-offer__card-text');
        this.configuration = this.querySelector('[data-offer-configuration]');
        this.picker = this.querySelector('[data-offer-picker]');
        this.quantityControl = this.querySelector('[data-offer-quantity]');
        this.summary = this.querySelector('[data-offer-summary]');
        this.slots = this.querySelector('[data-offer-slots]');
        this.slotTemplate = this.querySelector('[data-offer-slot-template]');
        this.addButton = this.querySelector('[data-offer-add]');
        this.quantity = 0;
        this.discount = 0;

        this.products = this.parseProducts();

        this.addEventListener('click', (event) => this.handleClick(event), {
          signal: this.abortController.signal,
        });
        this.addEventListener('change', (event) => this.handleChange(event), {
          signal: this.abortController.signal,
        });

        window.addEventListener('resize', () => this.scheduleCardCopyFit(), {
          signal: this.abortController.signal,
        });

        this.scheduleCardCopyFit();
        this.cardCopyFitTimers = [250, 1000].map((delay) => setTimeout(() => {
          if (this.isConnected) this.scheduleCardCopyFit();
        }, delay));
        document.fonts?.ready.then(() => {
          if (this.isConnected) this.scheduleCardCopyFit();
        });

        if (this.dialog) {
          this.dialog.addEventListener('click', (event) => {
            if (event.target === this.dialog) this.closeDialog();
          }, { signal: this.abortController.signal });
        }

        if (this.products.length === 0) {
          const openButton = this.querySelector('[data-offer-open]');
          if (openButton) openButton.disabled = true;
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
        if (this.cardCopyFitFrame) cancelAnimationFrame(this.cardCopyFitFrame);
        this.cardCopyFitTimers?.forEach((timer) => clearTimeout(timer));
      }

      scheduleCardCopyFit() {
        if (this.cardCopyFitFrame) cancelAnimationFrame(this.cardCopyFitFrame);

        this.cardCopyFitFrame = requestAnimationFrame(() => {
          this.cardCopyFitFrame = 0;
          this.fitSingleLine(this.cardHeading, 18);
          this.fitSingleLine(this.cardText, 8);
        });
      }

      fitSingleLine(element, minimumSize) {
        if (!element) return;

        element.style.removeProperty('font-size');

        const textRange = document.createRange();
        textRange.selectNodeContents(element);

        const availableWidth = element.getBoundingClientRect().width;
        const requiredWidth = textRange.getBoundingClientRect().width;

        if (availableWidth <= 0 || requiredWidth <= availableWidth) return;

        const preferredSize = Number.parseFloat(getComputedStyle(element).fontSize);
        const fittedSize = Math.max(minimumSize, preferredSize * availableWidth / requiredWidth * 0.97);
        element.style.fontSize = `${fittedSize.toFixed(2)}px`;
      }

      parseProducts() {
        try {
          const productData = JSON.parse(this.querySelector('[data-offer-products]')?.textContent || '[]');

          return productData
            .filter((product) => product.available)
            .map((product) => ({
              ...product,
              id: String(product.id),
              variants: (product.variants || [])
                .filter((variant) => variant.available)
                .map((variant) => ({ ...variant, id: String(variant.id) })),
            }))
            .filter((product) => product.variants.length > 0);
        } catch (error) {
          return [];
        }
      }

      handleClick(event) {
        if (event.target.closest('[data-offer-open]')) {
          this.openDialog();
          return;
        }

        if (event.target.closest('[data-offer-close]')) {
          this.closeDialog();
          return;
        }

        if (event.target.closest('[data-offer-add]')) {
          this.addOffer();
          return;
        }

        const clickedTier = event.target.closest('[data-offer-tier]');

        if (clickedTier && clickedTier === this.selectedTier) {
          if (!event.target.matches('[data-tier-input]')) event.preventDefault();
          this.clearTierSelection();
          return;
        }

        const quantityButton = event.target.closest('[data-quantity-minus], [data-quantity-plus]');

        if (!quantityButton || !this.selectedTier) return;

        const minimum = Number.parseInt(this.selectedTier.dataset.minimum || '1', 10);
        const unlimited = this.selectedTier.dataset.unlimited === 'true';
        const maximum = unlimited
          ? Number.POSITIVE_INFINITY
          : Math.max(minimum, Number.parseInt(this.selectedTier.dataset.maximum || minimum, 10));
        const change = quantityButton.hasAttribute('data-quantity-plus') ? 1 : -1;
        const nextQuantity = Math.min(maximum, Math.max(minimum, this.quantity + change));

        if (nextQuantity !== this.quantity) {
          this.quantity = nextQuantity;
          this.renderSlots(this.quantity);
          this.updateQuantityControl();
        }
      }

      handleChange(event) {
        if (event.target.matches('[data-tier-input]')) {
          this.selectTier(event.target.closest('[data-offer-tier]'));
          return;
        }

        const productSelect = event.target.closest('[data-product-select]');

        if (productSelect) {
          this.updateSlotProduct(productSelect.closest('.bastion-collection-offer__slot'), productSelect.value);
          this.updateSummary();
          return;
        }

        if (event.target.matches('[data-variant-select]')) {
          this.updateSummary();
        }
      }

      get selectedTier() {
        return this.querySelector('[data-offer-tier][data-selected]');
      }

      openDialog() {
        if (!this.dialog || this.products.length === 0) return;

        if (typeof this.dialog.showModal === 'function') {
          if (!this.dialog.open) this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }

        requestAnimationFrame(() => {
          (this.querySelector('[data-tier-input]:checked') || this.querySelector('[data-tier-input]'))?.focus();
        });
      }

      closeDialog() {
        if (!this.dialog) return;

        if (typeof this.dialog.close === 'function' && this.dialog.open) {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
        }
      }

      selectTier(tier) {
        if (!tier || !this.configuration) return;

        this.querySelectorAll('[data-offer-tier]').forEach((option) => {
          option.toggleAttribute('data-selected', option === tier);
        });

        this.quantity = Math.max(1, Number.parseInt(tier.dataset.minimum || '1', 10));
        this.discount = Math.max(0, Math.min(100, Number.parseFloat(tier.dataset.discount || '0')));
        tier.after(this.configuration);
        this.configuration.hidden = false;
        this.picker.hidden = false;
        this.summary.hidden = false;
        this.renderSlots(this.quantity);
        this.updateQuantityControl();
        this.clearStatus();
      }

      clearTierSelection() {
        this.querySelectorAll('[data-offer-tier]').forEach((tier) => {
          tier.removeAttribute('data-selected');
          const input = tier.querySelector('[data-tier-input]');
          if (input) input.checked = false;
        });

        this.quantity = 0;
        this.discount = 0;
        if (this.configuration) this.configuration.hidden = true;
        if (this.picker) this.picker.hidden = true;
        if (this.quantityControl) this.quantityControl.hidden = true;
        if (this.summary) this.summary.hidden = true;
        if (this.slots) this.slots.replaceChildren();
        if (this.addButton) this.addButton.disabled = true;
        this.clearStatus();
      }

      updateQuantityControl() {
        if (!this.selectedTier || !this.quantityControl) return;

        const minimum = Number.parseInt(this.selectedTier.dataset.minimum || '1', 10);
        const unlimited = this.selectedTier.dataset.unlimited === 'true';
        const maximum = unlimited
          ? Number.POSITIVE_INFINITY
          : Math.max(minimum, Number.parseInt(this.selectedTier.dataset.maximum || minimum, 10));
        const output = this.querySelector('[data-quantity-output]');
        const minusButton = this.querySelector('[data-quantity-minus]');
        const plusButton = this.querySelector('[data-quantity-plus]');

        this.quantityControl.hidden = minimum === maximum;
        if (output) output.textContent = String(this.quantity);
        if (minusButton) minusButton.disabled = this.quantity <= minimum;
        if (plusButton) plusButton.disabled = this.quantity >= maximum;
      }

      renderSlots(count) {
        if (!this.slots || !this.slotTemplate) return;

        const previousSelections = Array.from(this.slots.querySelectorAll('.bastion-collection-offer__slot')).map((slot) => ({
          productId: slot.querySelector('[data-product-select]')?.value || '',
          variantId: slot.querySelector('[data-variant-select]')?.value || '',
        }));

        this.slots.replaceChildren();

        for (let index = 0; index < count; index += 1) {
          const fragment = this.slotTemplate.content.cloneNode(true);
          const slot = fragment.querySelector('.bastion-collection-offer__slot');
          const productSelect = fragment.querySelector('[data-product-select]');
          const variantSelect = fragment.querySelector('[data-variant-select]');
          const productLabel = fragment.querySelector('[data-product-label]');
          const variantLabel = fragment.querySelector('[data-variant-label]');
          const productId = `${this.id}-product-${index + 1}`;
          const variantId = `${this.id}-variant-${index + 1}`;

          slot.dataset.slotIndex = String(index);
          fragment.querySelector('[data-slot-number]').textContent = String(index + 1);
          productSelect.id = productId;
          variantSelect.id = variantId;
          productLabel.htmlFor = productId;
          variantLabel.htmlFor = variantId;
          productLabel.textContent = `Pen ${index + 1}`;

          this.products.forEach((product) => {
            productSelect.add(new Option(product.title, product.id));
          });

          this.slots.append(fragment);

          const selection = previousSelections[index];
          const renderedSlot = this.slots.lastElementChild;

          if (selection?.productId && this.products.some((product) => product.id === selection.productId)) {
            renderedSlot.querySelector('[data-product-select]').value = selection.productId;
            this.updateSlotProduct(renderedSlot, selection.productId, selection.variantId);
          }
        }

        this.updateSummary();
      }

      updateSlotProduct(slot, productId, preferredVariantId = '') {
        if (!slot) return;

        const product = this.products.find((candidate) => candidate.id === String(productId));
        const variantField = slot.querySelector('[data-variant-field]');
        const variantSelect = slot.querySelector('[data-variant-select]');
        const image = slot.querySelector('[data-slot-image]');
        const number = slot.querySelector('[data-slot-number]');

        variantSelect.replaceChildren(new Option('Choose a variant', ''));

        if (!product) {
          variantSelect.disabled = true;
          variantField.hidden = true;
          image.hidden = true;
          image.removeAttribute('src');
          number.hidden = false;
          return;
        }

        product.variants.forEach((variant) => {
          variantSelect.add(new Option(variant.title, variant.id));
        });

        if (product.variants.length === 1) {
          variantSelect.value = product.variants[0].id;
          variantSelect.disabled = true;
          variantField.hidden = true;
        } else {
          variantSelect.disabled = false;
          variantField.hidden = false;

          if (product.variants.some((variant) => variant.id === String(preferredVariantId))) {
            variantSelect.value = String(preferredVariantId);
          }
        }

        if (product.image) {
          image.src = product.image;
          image.hidden = false;
          number.hidden = true;
        } else {
          image.hidden = true;
          number.hidden = false;
        }
      }

      selectedVariants() {
        return Array.from(this.slots?.querySelectorAll('[data-variant-select]') || []).map((select) => {
          const slot = select.closest('.bastion-collection-offer__slot');
          const productId = slot.querySelector('[data-product-select]')?.value || '';
          const product = this.products.find((candidate) => candidate.id === productId);
          return product?.variants.find((variant) => variant.id === select.value) || null;
        });
      }

      updateSummary() {
        if (!this.selectedTier || !this.summary) return;

        const variants = this.selectedVariants();
        const complete = variants.length === this.quantity && variants.every(Boolean);
        const originalPrice = variants.reduce((total, variant) => total + (variant?.price || 0), 0);
        const offerPrice = Math.round(originalPrice * (100 - this.discount) / 100);
        const savings = originalPrice - offerPrice;

        this.querySelector('[data-offer-original]').textContent = this.formatMoney(originalPrice);
        this.querySelector('[data-offer-savings]').textContent = this.formatMoney(savings);
        this.querySelector('[data-offer-total]').textContent = this.formatMoney(offerPrice);

        if (this.addButton?.getAttribute('aria-busy') !== 'true') {
          this.addButton.disabled = !complete;
        }
      }

      formatMoney(cents) {
        const currency = this.dataset.currencyCode || 'USD';
        const locale = document.documentElement.lang || 'en';

        try {
          return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
        } catch (error) {
          return `${currency} ${(cents / 100).toFixed(2)}`;
        }
      }

      buildItems() {
        const mergedItems = new Map();

        this.selectedVariants().forEach((variant) => {
          if (!variant) return;
          const current = mergedItems.get(variant.id) || { id: variant.id, quantity: 0 };
          current.quantity += 1;
          mergedItems.set(variant.id, current);
        });

        return Array.from(mergedItems.values());
      }

      async addOffer() {
        if (!this.addButton || this.addButton.getAttribute('aria-busy') === 'true') return;

        const variants = this.selectedVariants();

        if (variants.length !== this.quantity || variants.some((variant) => !variant)) {
          this.showError('Choose an available variant for every pen.');
          return;
        }

        const items = this.buildItems();
        const sectionsToBundle = new Set();
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        }));
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
        this.setBusy(true);
        this.clearStatus();

        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items, sections: Array.from(sectionsToBundle).join(',') }),
          });
          const responseJson = await response.json();

          if (!response.ok) {
            throw new Error(responseJson.description || responseJson.message || 'The offer could not be added to the cart.');
          }

          const cartResponse = await fetch(`${root}cart.js`);
          let cart = await cartResponse.json();
          const discountResult = await bastionApplyCollectionTierDiscount(
            this,
            '',
            Array.from(sectionsToBundle),
            cart
          );
          cart = discountResult.cart || cart;
          const discountError = discountResult.error;

          cart.sections ||= responseJson.sections;

          if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
            window.location.href = `${root}cart`;
            return;
          }

          this.dispatchEvent(new CustomEvent('variant:add', {
            bubbles: true,
            detail: { items: responseJson.items || [], cart },
          }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart },
          }));
          this.closeDialog();
          if (discountError) this.showError(discountError);
        } catch (error) {
          this.dispatchEvent(new CustomEvent('cart:error', {
            bubbles: true,
            detail: { error: error.message },
          }));
          this.showError(error.message);
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } finally {
          this.setBusy(false);
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        }
      }

      setBusy(isBusy) {
        if (!this.addButton) return;

        this.addButton.toggleAttribute('disabled', isBusy);
        this.addButton.setAttribute('aria-busy', String(isBusy));
        this.addButton.textContent = isBusy ? 'Adding...' : (this.dataset.addLabel || 'Add offer to cart');

        if (!isBusy) this.updateSummary();
      }

      clearStatus() {
        const status = this.querySelector('[data-offer-status]');
        if (!status) return;
        status.removeAttribute('data-error');
        status.textContent = '';
      }

      showError(message) {
        const status = this.querySelector('[data-offer-status]');
        if (status) {
          status.dataset.error = '';
          status.textContent = message;
        }
        document.dispatchEvent(new CustomEvent('toast:show', {
          detail: { message, tone: 'error' },
        }));
      }
    }
  );
}
